import * as React from "react";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { getCreateTemplate, getOutwardDetail } from "../api/saleOutwardApi";
import type { SaleSourceType } from "../config/saleKinds";
import type { OutwardRoute } from "../routing/resolveOutwardRoute";
import { mergeOrderLines } from "../state/mergeOrderLines";
import type { SaleOutwardAction } from "../state/saleOutwardReducer";
import type { ScannedSerialRow } from "../types";

/**
 * Tải dữ liệu phiếu một lần khi mở trang:
 * - Tạo mới từ phiếu cần xuất (có sourceCode): lấy mẫu tạo và mã phiếu XK.
 * - Sửa / xem (có id hoặc transactionCode): lấy chi tiết phiếu và các serial đã lưu.
 * Kết quả của lần tải cũ bị bỏ qua nếu tham số đổi trước khi response về.
 */
export const useSaleOutwardData = (
  route: OutwardRoute,
  sourceType: SaleSourceType,
  dispatch: React.Dispatch<SaleOutwardAction>,
) => {
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const requestRef = React.useRef(0);

  const { action, sourceCode, id, transactionCode } = route;

  React.useEffect(() => {
    const requestId = ++requestRef.current;
    const isCurrent = () => requestId === requestRef.current;

    const load = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        if (id || transactionCode) {
          const mode = action === "EDIT" ? "edit" : "detail";
          const res = await getOutwardDetail(mode, { id, transactionCode });
          if (!isCurrent()) return;
          if (!res?.success || !res.data) {
            setLoadError(true);
            return;
          }
          const detail = res.data;
          dispatch({
            type: "LOAD_DETAIL",
            header: detail.tblInventoryTransactionModel,
            // Xuất đổi trả: serial khách trả đi kèm model, gộp vào order để lưới chọn "Serial nhập"
            order: { ...(detail.tblInventoryTransactionModel as any), returnSerials: detail.returnSerials ?? [] },
            orderLines: mergeOrderLines(detail.inventoryTransactionRequestSourceModels),
            rows: (detail.inventoryTransactionSerialModels ?? []).map(
              (row): ScannedSerialRow => ({ ...row, type: "scanned" }),
            ),
          });
          return;
        }

        if (sourceCode && action === "CREATE") {
          const res = await getCreateTemplate(sourceCode, sourceType);
          if (!isCurrent()) return;
          if (!res?.success || !res.data) {
            setLoadError(true);
            return;
          }
          const model = res.data.tblInventoryTransactionModel ?? {};
          dispatch({
            type: "LOAD_TEMPLATE",
            order: { ...model, returnSerials: res.data.returnSerials ?? [] },
            orderLines: mergeOrderLines(res.data.inventoryTransactionRequestSourceModels),
            headerPatch: {
              attribute11: model?.toBranchId?.toString() ?? "",
              attribute12: "",
              fromInvId: model?.fromInvId ?? 0,
              transactionCode: model?.transactionCode,
              ecommerceOrderId: model?.ecommerceOrderId ?? null,
              sourceDescription: model?.sourceDescription ?? null,
            },
          });
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error("[SaleOutward] tải dữ liệu lỗi", error);
        setLoadError(true);
        NotificationExtension.Fails("Không tải được dữ liệu phiếu xuất.");
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    load();
  }, [action, sourceCode, id, transactionCode, sourceType, dispatch]);

  return { loading, loadError };
};
